import { FC } from "react";
import { Box, styled, useTheme } from "@mui/system";
import muiIcons from "../../../components/icons/mui-icons";
import Containers from "../../../components/Containers";
import { Avatar, Grid, IconButton } from "@mui/material";
import { CardContainer } from "../../../components/CardContainer";
import Breadcrumb from "../../../components/breadcrumb/Breadcrumb";
import { GullButton } from "../../../components/buttons/GullButton";
import ColumnChart from "../../../components/apexCharts/ColumnChart";
import { H2, H3, H4, H6, Paragraph } from "../../../components/Typography";
import ColumnChart2 from "../../../components/apexCharts/ColumnChart2";
import ThreeColumnChart from "../../../components/apexCharts/ThreeColumnChart";
import LinearProgress, {
  linearProgressClasses,
} from "@mui/material/LinearProgress";
import {
  userCardData,
  authorListData,
  productCardData,
  developerListData,
} from "./dashboard-data";

const TopChart = styled(Box)(({ theme }) => ({
  "& h2": {
    marginTop: "-20px",
    fontWeight: 600,
  },
  "& p": {
    fontWeight: 600,
    color: theme.palette.grey.main,
  },
  [theme.breakpoints.down("md")]: {
    width: "calc(100vw - 60px)",
  },
}));

const StyledAvatar = styled(Avatar)(() => ({
  width: 70,
  height: 70,
  margin: "auto",
  marginTop: "5px",
  marginBottom: "10px",
}));

const AuthorCardBody = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: "15px 0px",
  borderBottom: `1px solid ${theme.palette.grey[300]}`,
  "&:last-child": {
    border: "none",
  },
}));

const DeveloperCardBody = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: "15px 0px",
  [theme.breakpoints.down("md")]: {
    display: "block",
  },
  "& #content": {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    [theme.breakpoints.down("sm")]: {
      display: "block",
      padding: "10px 0px",
    },
    "& #progressBox": {
      width: "78%",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      [theme.breakpoints.down("sm")]: {
        width: "100%",
        display: "block",
      },
      "& #progress": {
        width: "80%",
        margin: "10px 0px",
        [theme.breakpoints.down("sm")]: {
          width: "100%",
        },
      },
    },
  },
}));

const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
  height: 10,
  borderRadius: 5,
  [`& .${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: theme.palette.grey[400],
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 5,
    backgroundColor: theme.palette.primary[400],
  },
}));

const ProductCard = styled(Box)(({ theme }) => ({
  display: "flex",
  "& img": {
    width: 250,
    marginBottom: "-6px",
  },
  [theme.breakpoints.down("md")]: {
    "& img": {
      width: 200,
    },
  },
  [theme.breakpoints.down("sm")]: {
    "& img": {
      width: 180,
    },
  },
}));

const ProductContent = styled(Box)(() => ({
  padding: "1.5rem 1.1rem",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
}));

const Dashboard3: FC = () => {
  const theme = useTheme();
  const { ChartContainer } = Containers();
  const chartData = [10, 20, 30, 40, 50, 60, 70, 60, 50, 40, 30, 20, 10];
  const chart2Data = [55, 43, 35, 60, 50, 40, 35, 37, 48];
  const chartColor = [
    theme.palette.primary.main,
    theme.palette.secondary.main,
    theme.palette.success.main,
    theme.palette.warning.main,
  ];
  const sideCardData = [
    {
      name: "Backups",
      subName: "Total: 12",
      Icon: muiIcons.ServerIcon,
      color: theme.palette.primary.main,
    },
    {
      name: "Databases",
      subName: "Total: 12",
      Icon: muiIcons.DatabaseIcon,
      color: theme.palette.secondary.main,
    },
    {
      name: "Space Used",
      subName: "Total: 160GB",
      Icon: muiIcons.InboxIcon,
      color: theme.palette.success.main,
    },
    {
      name: "Downloaded",
      subName: "Total: 30GB",
      Icon: muiIcons.DownloadIcon,
      color: theme.palette.warning.main,
    },
  ];

  return (
    <>
      <Breadcrumb name="Dashboard" subName="Version 3" path="/dashboard/v1" />
      <Grid container spacing={3}>
        {chartColor.map((color) => (
          <Grid item xs={12} md={3}>
            <ChartContainer
              sx={{ padding: 0, paddingBottom: "15px", textAlign: "center" }}
            >
              <TopChart>
                <ColumnChart chartData={chartData} color={color} />
                <H2 color={color}>6,708</H2>
                <Paragraph>50% Average</Paragraph>
              </TopChart>
            </ChartContainer>
          </Grid>
        ))}

        <Grid item xs={12} md={6}>
          <Grid container spacing={3}>
            {sideCardData.map((data) => {
              const { name, subName, Icon, color } = data;
              return (
                <Grid item xs={12} sm={6} md={6}>
                  <CardContainer
                    sx={{
                      display: "flex",
                      padding: "45px 10px",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Icon sx={{ color: color, fontSize: 45, mr: "10px" }} />
                    <Box>
                      <H4>{name}</H4>
                      <Paragraph>{subName}</Paragraph>
                    </Box>
                  </CardContainer>
                </Grid>
              );
            })}
          </Grid>
        </Grid>

        <Grid item xs={12} md={6}>
          <ChartContainer sx={{ pb: 0, pl: "5px" }}>
            <ThreeColumnChart height={260} />
          </ChartContainer>
        </Grid>

        <Grid item xs={12} md={6}>
          <ChartContainer sx={{ pb: 0, pl: "5px" }}>
            <Box
              px="10px"
              pl="20px"
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <H6>Last 7 Days Traffic Status</H6>
              <H3>15%</H3>
            </Box>
            <ColumnChart2
              chartData={chart2Data}
              color={theme.palette.primary.main}
            />
          </ChartContainer>
        </Grid>

        <Grid item xs={12} md={6}>
          <ChartContainer sx={{ pb: 0, pl: "5px" }}>
            <Box
              px="10px"
              pl="20px"
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <H6>Server Failures</H6>
              <H3>15%</H3>
            </Box>
            <ColumnChart2
              chartData={chart2Data}
              color={theme.palette.warning.main}
            />
          </ChartContainer>
        </Grid>

        <Grid item xs={12} md={5}>
          <Grid container spacing={3}>
            {userCardData.map((data) => {
              const { photoUrl, name, position } = data;
              const { FacebookIcon, TwitterIcon, InstagramIcon } = muiIcons;
              return (
                <Grid item xs={12} sm={6} md={6}>
                  <CardContainer sx={{ textAlign: "center" }}>
                    <StyledAvatar src={photoUrl} />
                    <H6>{name}</H6>
                    <Paragraph sx={{ fontSize: 12 }}>{position}</Paragraph>
                    <Paragraph sx={{ my: "6px" }}>
                      Create usable interface and designs@uilib.com
                    </Paragraph>
                    <GullButton
                      rounded={true}
                      variant="contained"
                      color="primary"
                    >
                      Hire me
                    </GullButton>
                    <Box>
                      <IconButton>
                        <FacebookIcon />
                      </IconButton>
                      <IconButton>
                        <TwitterIcon />
                      </IconButton>
                      <IconButton>
                        <InstagramIcon />
                      </IconButton>
                    </Box>
                  </CardContainer>
                </Grid>
              );
            })}

            <Grid item xs={12}>
              <ChartContainer>
                <H4 fontWeight={600} mb={1}>
                  Author Profit
                </H4>
                {authorListData.map((data) => {
                  const { name, photoUrl, position, profit } = data;
                  return (
                    <AuthorCardBody>
                      <Avatar sx={{ mr: "10px" }} src={photoUrl} alt="" />
                      <Box
                        width="100%"
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                      >
                        <Box>
                          <H6>{name}</H6>
                          <Paragraph>{position}</Paragraph>
                        </Box>
                        <Box>
                          <H3 sx={{ color: "primary.main" }}>${profit}+</H3>
                        </Box>
                      </Box>
                    </AuthorCardBody>
                  );
                })}
              </ChartContainer>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12} md={7}>
          <Grid container spacing={3}>
            {productCardData.map((data) => {
              const { name, price, photoUrl, store, Icon, details } = data;
              return (
                <Grid item xs={12}>
                  <CardContainer sx={{ padding: 0, display: "flex" }}>
                    <ProductCard>
                      <img src={photoUrl} alt="" />
                      <ProductContent>
                        <Box>
                          <H4>{name}</H4>
                          <Paragraph color="grey.main">{store}</Paragraph>
                        </Box>
                        <Box>
                          <H2 color="primary.main">{`$${price}.00`}</H2>
                          <Paragraph>{details}</Paragraph>
                        </Box>
                        <Box>
                          <IconButton>
                            <Icon
                              sx={{ color: "primary.main", fontSize: 28 }}
                            />
                          </IconButton>
                        </Box>
                      </ProductContent>
                    </ProductCard>
                  </CardContainer>
                </Grid>
              );
            })}

            <Grid item xs={12}>
              <ChartContainer>
                <H4 fontWeight={600} mb="15px">
                  Author Profit
                </H4>
                {developerListData.map((data) => {
                  const { name, photoUrl, position } = data;
                  return (
                    <DeveloperCardBody>
                      <Avatar sx={{ mr: "10px" }} src={photoUrl} alt="" />
                      <Box id="content">
                        <Box>
                          <H6>{name}</H6>
                          <Paragraph>{position}</Paragraph>
                        </Box>
                        <Box id="progressBox">
                          <Box id="progress">
                            <BorderLinearProgress
                              variant="determinate"
                              value={65}
                            />
                          </Box>
                          <GullButton
                            rounded={true}
                            variant="outlined"
                            color="primary"
                            sx={{ padding: ".1rem 1rem" }}
                          >
                            Follow
                          </GullButton>
                        </Box>
                      </Box>
                    </DeveloperCardBody>
                  );
                })}
              </ChartContainer>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

export default Dashboard3;
